import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import EmptyState from "@/components/base/EmptyState";
import { useSeo } from "@/hooks/useSeo";
import { newsList } from "@/mocks/news";

export default function NewsDetail() {
  const { id } = useParams();
  const article = newsList.find((item) => item.id === id);

  useSeo({
    title: article ? `${article.title} - 樱花动漫官网` : "资讯详情 - 樱花动漫官网",
    description: article ? article.excerpt : "樱花动漫官网资讯详情页，提供动漫行业最新动态与深度内容。",
    keywords: article ? article.tags.join(",") : "动漫资讯,樱花动漫官网",
  });

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="mx-auto w-full max-w-[900px] px-4 py-20 md:px-8">
          <EmptyState
            title="这篇资讯不见了"
            description="可能链接已经失效。回到资讯列表，看看今天更新的其他内容吧。"
            secondaryLabel="返回资讯列表"
            secondaryTo="/news"
          />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = newsList
    .filter((item) => item.id !== article.id && item.category === article.category)
    .slice(0, 3);
  const fallbackRelated = newsList.filter((item) => item.id !== article.id).slice(0, 3);
  const relatedList = related.length > 0 ? related : fallbackRelated;

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <article>
          <div className="mx-auto w-full max-w-[880px] px-4 pt-8 md:px-8 md:pt-10">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
              <Link to="/news" className="transition-colors hover:text-primary-600">
                动漫资讯
              </Link>
              <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
              <span className="truncate text-foreground-800">{article.category}</span>
            </nav>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="rounded bg-primary-100 px-2.5 py-1 font-label text-[11px] text-primary-700">
                {article.category}
              </span>
              <span className="font-label text-xs text-foreground-500">{article.date}</span>
              <span className="flex items-center gap-1 text-xs text-foreground-500">
                <i className="ri-eye-line" />
                {article.views} 阅读
              </span>
            </div>

            <h1 className="mt-3 text-2xl font-bold leading-snug text-foreground-950 md:text-[34px]">
              {article.title}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-[15px]">{article.excerpt}</p>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-y border-background-200 py-3.5">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-label text-xs text-secondary-900">
                  {article.author.slice(-2, -1)}
                </span>
                <div>
                  <p className="text-sm text-foreground-900">{article.author}</p>
                  <p className="text-[11px] text-foreground-500">樱花动漫编辑部</p>
                </div>
              </div>
              <Link
                to="/news"
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-arrow-left-line" />
                返回资讯列表
              </Link>
            </div>
          </div>

          <div className="mx-auto mt-6 w-full max-w-[880px] px-4 md:px-8">
            <div className="aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
              <img
                src={article.cover}
                alt={`${article.title} 配图`}
                title={`${article.title} - 动漫资讯配图`}
                className="h-full w-full object-cover object-center"
              />
            </div>
          </div>

          <div className="mx-auto w-full max-w-[880px] px-4 py-8 md:px-8">
            <div className="space-y-5">
              {article.body.map((paragraph, index) => (
                <p
                  key={`${article.id}-p-${index}`}
                  className="text-[15px] leading-8 text-foreground-800"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
              <span className="text-xs text-foreground-500">相关标签</span>
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}&type=news`}
                  className="whitespace-nowrap rounded-md bg-background-100 px-2.5 py-1 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
                >
                  # {tag}
                </Link>
              ))}
            </div>

            <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="text-sm font-bold text-foreground-950">声明</h2>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                本文为樱花动漫官网演示内容，其中人物、公司名称与数据均为虚构示例，仅用于展示资讯页面的排版与结构，不构成任何真实报道。
              </p>
            </div>
          </div>
        </article>

        <section className="border-t border-background-200 bg-background-100 py-10 md:py-12">
          <div className="mx-auto w-full max-w-[1400px] px-4 md:px-8">
            <div className="flex items-end justify-between">
              <div>
                <span className="font-label text-[11px] uppercase tracking-[0.2em] text-primary-600">
                  Keep reading
                </span>
                <h2 className="mt-2 text-xl font-bold text-foreground-950 md:text-2xl">继续阅读</h2>
              </div>
              <Link
                to="/news"
                className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-600"
              >
                全部资讯
                <i className="ri-arrow-right-line text-sm" />
              </Link>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {relatedList.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group overflow-hidden rounded-lg border border-background-200 bg-background-50"
                >
                  <div className="aspect-[16/9] w-full overflow-hidden bg-background-200">
                    <img
                      src={item.cover}
                      alt={`${item.title} 封面`}
                      title={`${item.title} - 资讯`}
                      className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2">
                      <span className="rounded bg-secondary-100 px-1.5 py-0.5 font-label text-[10px] text-secondary-900">
                        {item.category}
                      </span>
                      <span className="font-label text-[11px] text-foreground-500">{item.date}</span>
                    </div>
                    <h3 className="mt-2 line-clamp-2 text-sm font-medium leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                      {item.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}